var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Live MARC preview panel for the proposal form.
 * Subscribes to form state via watch(callback) subscription API,
 * converts form data through the pipeline:
 * GenericFormState -> ProposalDataField[] -> RecordDataField[],
 * and renders MarcDataDisplay.
 *
 * Uses watch(callback) instead of watch() in render body to avoid
 * unnecessary re-renders — only setDebouncedFields triggers a render.
 * Debounces the conversion by 200ms to coalesce rapid form input.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { genericFormToProposalData } from './utils/genericFormToProposalData';
import { proposalDataToRecordDataFields } from './utils/proposalDataToRecordDataFields';
import { filterEmptyPreviewFields } from './utils/filterEmptyPreviewFields';
import { controlFieldRow, isControlFieldTag } from '../../utils/marcControlFields';
import { derivedControlFieldSignature } from './utils/derivedControlFieldSignature';
import { useDerivedControlFields } from '../../hooks/useDerivedControlFields';
import { recordToMarc } from '../../utils/recordToMarc';
import MarcDataDisplay from '../RecordDetail/MarcDataDisplay';
import { TAB_BAR_STICKY_PX } from './stickyLayout';
var headerStyle = {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: '12px',
    color: '#333',
};
var placeholderStyle = {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#888',
    fontSize: '0.9rem',
    fontStyle: 'italic',
};
// MARC preview is always shorter than the edit form, so the panel just sticks
// to the top of the right column and the user scrolls the page past it.
// No maxHeight / no inner scroll — clipping (TASK-088) was caused by those.
//
// The top offset is the EXPECTED height of RecordForm's sticky tab bar, imported
// from the module that also builds that bar's style. That import removes the
// duplicated literal, not the risk: only the bar's padding is genuinely shared,
// the button height is an assumption nothing enforces. See `stickyLayout.ts`.
var panelContainerStyle = {
    position: 'sticky',
    top: TAB_BAR_STICKY_PX,
};
/** Stable identity so the default prop value cannot re-trigger the memo. */
var EMPTY_CONTROL_FIELDS = [];
/**
 * Live MARC preview panel that renders MarcDataDisplay
 * with data derived from the current form state.
 */
var MarcPreviewPanel = function (_a) {
    var _b, _c;
    var config = _a.config, watch = _a.watch, _d = _a.controlFields, controlFields = _d === void 0 ? EMPTY_CONTROL_FIELDS : _d, recordType = _a.recordType;
    var _e = useState([]), debouncedFields = _e[0], setDebouncedFields = _e[1];
    var timerRef = useRef(undefined);
    // Subscribe via watch(callback) — does not cause re-renders on form changes.
    // Only setDebouncedFields triggers a targeted re-render of this component.
    useEffect(function () {
        var computeAndSet = function (formData) {
            var proposalData = genericFormToProposalData(config, formData);
            var recordDataFields = proposalDataToRecordDataFields(proposalData);
            var filteredFields = filterEmptyPreviewFields(recordDataFields, config);
            setDebouncedFields(filteredFields);
        };
        // Initial preview from current form values (edit mode loads with initialData —
        // watch callback only fires on subsequent changes, not on mount).
        computeAndSet(watch());
        var subscription = watch(function (formData) {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(function () {
                computeAndSet(formData);
            }, 200);
        });
        return function () {
            clearTimeout(timerRef.current);
            subscription.unsubscribe();
        };
    }, [config, watch]);
    // CZNA-504: control fields come from the loaded record, everything else from
    // the form. The form-derived side is filtered rather than trusted to be free
    // of 00X, and since CZNA-657 that filter is load-bearing rather than
    // belt-and-braces: `genericFormToProposalData` now DOES re-emit control
    // fields, because the form never writes them (CZNA-455) while
    // `PUT /records/{id}` is a full replace, so omitting them from the payload
    // deleted them. They arrive here as passthrough rows and would duplicate the
    // record-derived row above — and a duplicated 001 reads as corrupt data.
    // MarcDataDisplay sorts control fields to the top itself.
    //
    // Everything else CZNA-657 preserves is deliberately NOT filtered out. A
    // `550`/`665` the template does not declare, or an Aleph pseudo-field
    // (`R00`, `R10`, … — 62 615 rows in the corpus), now shows up here, which is
    // the point: the preview renders the same `genericFormToProposalData` output
    // that gets saved, so it is the one place a cataloger can see that those
    // fields are still there. Filtering them would restore the exact mismatch
    // between preview and payload that CZNA-657 was raised over, only mirrored.
    // CZNA-684 — what the system will supply, asked for on the SAME rows the preview is
    // about to render. Keyed on a signature that ignores the heading's text (see
    // `derivedControlFieldSignature`), so typing a name does not issue a request; only a
    // change the derivation actually reads does.
    var formDerivedFields = useMemo(function () { return debouncedFields.filter(function (f) { return !isControlFieldTag(f.fieldTag); }); }, [debouncedFields]);
    var derivationInput = useMemo(function () { return __spreadArray(__spreadArray([], controlFields, true), debouncedFields, true); }, [controlFields, debouncedFields]);
    var signature = derivedControlFieldSignature(derivationInput);
    var derivedQuery = useDerivedControlFields(recordType, function () { return recordToMarc(derivationInput); }, signature, !!recordType);
    var derivedRows = (_c = (_b = derivedQuery.data) === null || _b === void 0 ? void 0 : _b.controlFields) !== null && _c !== void 0 ? _c : [];
    var previewFields = useMemo(function () {
        var rows = __spreadArray([], controlFields, true);
        var present = new Set(controlFields.map(function (f) { return f.fieldTag; }));
        for (var _i = 0, derivedRows_1 = derivedRows; _i < derivedRows_1.length; _i++) {
            var derived = derivedRows_1[_i];
            if (!present.has(derived.fieldTag)) {
                rows.push(controlFieldRow(derived.fieldTag, derived.value));
                present.add(derived.fieldTag);
            }
        }
        return __spreadArray(__spreadArray([], rows, true), formDerivedFields, true);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlFields, formDerivedFields, JSON.stringify(derivedRows)]);
    return (React.createElement("div", { style: panelContainerStyle, "data-testid": "marc-preview-panel" },
        React.createElement("div", { style: headerStyle },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.marcPreview" })),
        previewFields.length > 0 ? (React.createElement(MarcDataDisplay, { dataFields: previewFields, noAccordion: true })) : (React.createElement("div", { style: placeholderStyle, "data-testid": "marc-preview-placeholder" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.marcPreview.empty" })))));
};
export default MarcPreviewPanel;
